import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, RotateCcw, BellRing, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { STAGE_META } from "@/lib/realestateConstants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const REMINDER_OPTIONS = [
  [0, "Sin aviso"], [15, "15 min antes"], [30, "30 min antes"], [60, "1 hora antes"], [120, "2 horas antes"],
  [180, "3 horas antes"], [1440, "El día anterior (misma hora)"],
];

export const RealEstateSettingsTab = ({ ws }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["realestate-schedule", ws.id], queryFn: async () => (await api.get(`/workspaces/${ws.id}/realestate/schedule`)).data });
  const [sched, setSched] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setSched(data.schedule); }, [data]);
  if (!sched) return null;

  const stages = ["preventa", "venta", "postventa"];
  const set = (k, field, v) => setSched({ ...sched, [k]: { ...sched[k], [field]: v === "" ? "" : Math.max(0, parseInt(v, 10) || 0) } });
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(sched).map(([k, v]) => [k, { start_offset: Number(v.start_offset) || 0, due_offset: Number(v.due_offset) || 0 }]));
      const { data: r } = await api.put(`/workspaces/${ws.id}/realestate/schedule`, { schedule: payload });
      setSched(r.schedule);
      qc.invalidateQueries({ queryKey: ["realestate-schedule", ws.id] });
      toast.success("Plazos guardados. Se aplicarán a los nuevos inmuebles.");
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const reset = async () => {
    try {
      const { data: r } = await api.put(`/workspaces/${ws.id}/realestate/schedule`, { schedule: {} });
      setSched(r.schedule);
      toast.success("Plazos restablecidos");
    } catch (err) { toast.error(apiError(err)); }
  };
  const setReminder = async (v) => {
    try {
      await api.put(`/workspaces/${ws.id}/realestate/reminder-default`, { minutes: Number(v) });
      qc.invalidateQueries({ queryKey: ["realestate-schedule", ws.id] });
      toast.success("Aviso por defecto actualizado");
    } catch (err) { toast.error(apiError(err)); }
  };
  const total = Math.max(...Object.values(sched).map((v) => Number(v.due_offset) || 0));

  return (
    <div className="space-y-6" data-testid="realestate-settings-tab">
      <Card className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><CalendarRange size={18} /></span>
          <div className="flex-1">
            <h3 className="font-semibold">Plantilla de plazos</h3>
            <p className="text-xs text-muted-foreground">Días desde el alta del inmueble en los que empieza y termina cada fase. Define el ritmo real de tu agencia: cada nuevo inmueble se planificará así en el Gantt y el calendario. Duración total actual: <strong>{total} días</strong>.</p>
          </div>
        </div>
        <div className="space-y-4">
          {stages.map((st) => (
            <div key={st} className="space-y-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: STAGE_META[st].color }}>{STAGE_META[st].label}</p>
              <div className="grid grid-cols-[1fr_90px_90px] gap-2 text-xs items-center">
                <span className="text-muted-foreground">Fase</span><span className="text-muted-foreground text-center">Inicio (día)</span><span className="text-muted-foreground text-center">Límite (día)</span>
                {Object.entries(sched).filter(([, v]) => v.stage === st).map(([k, v]) => (
                  <div key={k} className="contents">
                    <span className="font-medium">{v.label}</span>
                    <Input type="number" min="0" value={v.start_offset} onChange={(e) => set(k, "start_offset", e.target.value)} className="h-8 text-xs text-center" data-testid={`sched-start-${k}`} />
                    <Input type="number" min="0" value={v.due_offset} onChange={(e) => set(k, "due_offset", e.target.value)} className="h-8 text-xs text-center" data-testid={`sched-due-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={reset} data-testid="sched-reset-btn"><RotateCcw size={14} className="mr-1" /> Valores por defecto</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="sched-save-btn"><Save size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar plazos"}</Button>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><BellRing size={18} /></span>
          <div className="flex-1">
            <h3 className="font-semibold">Aviso de visitas por defecto</h3>
            <p className="text-xs text-muted-foreground">Recordatorio por Telegram, push y bandeja de entrada antes de cada visita, con nombre y teléfono del visitante. Se puede cambiar visita a visita.</p>
          </div>
        </div>
        <Select value={String(data?.default_reminder_minutes ?? 60)} onValueChange={setReminder}>
          <SelectTrigger className="w-64 h-9" data-testid="reminder-default-select"><SelectValue /></SelectTrigger>
          <SelectContent>{REMINDER_OPTIONS.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </Card>
    </div>
  );
};
